import { mockConfig } from '@/mocks/mockConfig';
import { useConfigStore } from '@/store/config.store';
import { loadFonts } from '@/utils/loadFonts';
import { EUserName } from '@/typescript/enums/EUserName';
import { i18n } from '@/translations/i18n';
import { useTemplateStore } from '@/store/template.store';
import { getTemplate } from '@/api/template.api';
import { initTemplateImages } from '@/utils/templateImage';
import { getEmptyTemplate } from '@/utils/emptyTemplate';
import { loadFromLocalStorage } from '@/utils/localStorageSave';
import { Events } from '@/services/Events';
import { getProduct } from '@/api/product.api';
import { loadAndPrepareCutField } from '@/littleWedding/CutFieldController';
import { EProductFormat } from '@/typescript/enums/EProductFormat';
import { convertMMToPixel } from '@/utils/convertmmPixel';
import ObsidianApi from 'obsidian-api';
import type { IAppConfig } from '@/typescript/interfaces/IAppConfig';
import type { IProduct } from '@/typescript/interfaces/IProduct';

const MOCKED_PRODUCT: IProduct = {
    id: 1,
    name: 'Sample Product',
    width: 210,
    height: 297,
    format: EProductFormat.RECTO_VERSO,
    isColorsLocked: false,
    images: <never>[],
    isActive: true,
    type: 1,
};

async function initStore() {
    Events.emit('loading-animation-only-start', undefined);

    let config = ObsidianApi.$getConfig() as IAppConfig;
    if (Object.keys(config).length === 0) {
        config = mockConfig();
    }

    initConfigStoreFromConfig(config);

    const configStore = useConfigStore();
    i18n.global.locale.value = configStore.lang;

    const isMocked = false; // set to true to use mocked product data

    const initPromises = [
        initProductInStore(isMocked ? MOCKED_PRODUCT : undefined).then((p) => initCutPathInStore(p)),
        initTemplateInStore(config),
    ];

    if (!configStore.isProductEdition) {
        initPromises.push(initFontInStore());
    }

    return Promise.all(initPromises).then(() => {
        configStore.isStoreInitialized = true;
        Events.emit('loading-animation-only-end', undefined);
    });
}

function initConfigStoreFromConfig(config: IAppConfig) {
    const configStore = useConfigStore();
    configStore.token = config.token;
    configStore.isMobile = config.is_mobile;
    configStore.isAdmin = !!config.is_admin;
    configStore.userName = configStore.isAdmin ? EUserName.ADMIN : EUserName.USER;
    configStore.productType = config.product_type;
    configStore.lang = config.lang;
    configStore.projectId = config.project_id;
    configStore.isProductEdition = configStore.isAdmin && !config.theme_name;
}

async function initProductInStore(mockedProduct?: IProduct) {
    const configStore = useConfigStore();

    const product = mockedProduct ?? (await getProduct(configStore.productType));

    configStore.width = convertMMToPixel(product.width);
    configStore.height = convertMMToPixel(product.height);
    configStore.printHeight = product.height;
    configStore.printWidth = product.width;
    configStore.productId = product.id;
    configStore.isColorsLocked = product.isColorsLocked;
    configStore.productFormat = product.format;
    configStore.productImages = product.images;
    configStore.productName = product.name;

    return product;
}

async function initTemplateInStore(config: IAppConfig) {
    const configStore = useConfigStore();
    const templateStore = useTemplateStore();

    if (configStore.isProductEdition) {
        templateStore.selectedTemplate = getEmptyTemplate(true);
        return;
    }

    let localConfig;
    if (configStore.projectId && !configStore.isAdmin) {
        localConfig = await loadFromLocalStorage(configStore.projectId);
    }

    if (config.theme_name && !configStore.projectId) {
        templateStore.selectedTemplate = await initTemplateImages(await getTemplate(config.product_type, config.theme_name));
    } else if (configStore.projectId) {
        templateStore.selectedTemplate = localConfig ?? getEmptyTemplate();
    } else if (configStore.isAdmin) {
        const emptyProject = getEmptyTemplate();
        // emptyProject.configuration.fields.length = 0;
        templateStore.selectedTemplate = emptyProject;
    }
}

async function initFontInStore() {
    const configStore = useConfigStore();
    configStore.fonts = await loadFonts();
}

async function initCutPathInStore(product: IProduct) {
    const configStore = useConfigStore();
    const newImageFieldList = await loadAndPrepareCutField(product);
    configStore.cutpathFieldList = newImageFieldList;
}

export { initStore };
